// Audience management constants
import type { AudienceGroupStatus } from "../types/audience.types";

// Single source of truth for audience status values from LINE Bot SDK
export const AUDIENCE_STATUS_VALUES = [
	"IN_PROGRESS",
	"READY",
	"FAILED",
	"EXPIRED",
	"INACTIVE",
	"ACTIVATING",
] as const satisfies readonly AudienceGroupStatus[];

// Default configuration values
export const AUDIENCE_DEFAULTS = {
	MAX_PER_PAGE: 20,
	DEFAULT_SORT: "desc" as const,
	DEFAULT_SORT_KEY: "createdAt",
	REFRESH_INTERVAL: 30000, // 30 seconds
	MAX_RETRIES: 3,
	RATE_LIMIT_WINDOW: 60000, // 1 minute
	RATE_LIMIT_MAX: 10,
} as const;

// LINE API limits
export const LINE_AUDIENCE_LIMITS = {
	MAX_DESCRIPTION_LENGTH: 120,
	MAX_AUDIENCE_SIZE: 10000,
	MIN_AUDIENCE_SIZE: 1,
	MAX_AUDIENCES_PER_ACCOUNT: 1000,
} as const;

// Audience status mapping - dynamically generated from AUDIENCE_STATUS_VALUES
export const AUDIENCE_STATUS = Object.fromEntries(
	AUDIENCE_STATUS_VALUES.map((status) => [status, status]),
) as { readonly [K in (typeof AUDIENCE_STATUS_VALUES)[number]]: K };

// Filter types for member search
export const MEMBER_FILTER_TYPES = {
	ADDRESS: "address",
	MEMBER_SINCE: "memberSince",
	CAMPAIGN: "isCampaign",
	WINNER_STATUS: "hasWon",
	CANDIDATE_DATE: "candidateAt",
	CREATED_DATE: "createdAt",
	LAST_VISIT: "lastVisit",
	MESSAGE_STATUS: "messages",
	FRIEND_STATUS: "isFriends",
	REGISTRATION_STATUS: "isRegistered",
} as const;

// Message status options
export const MESSAGE_STATUS_OPTIONS = {
	READ: "read",
	UNREAD: "unread",
} as const;

// Error messages
export const AUDIENCE_ERROR_MESSAGES = {
	INVALID_AUDIENCE_NAME: "Audience name is required and must not be empty",
	DUPLICATE_AUDIENCE_NAME: "オーディエンス名が重複しています",
	NO_LINE_IDS_FOUND: "No LINE IDs found for the specified criteria",
	EXCEEDS_MAX_AUDIENCE_SIZE:
		"オーディエンスは10,000人を超えて作成できません。検索条件を絞り込んでください。",
	AUDIENCE_NOT_FOUND: "Audience group not found",
	LINE_API_ERROR: "LINE API error occurred",
	DATABASE_ERROR: "Database operation failed",
	INVALID_SEARCH_PARAMS: "Invalid search parameters provided",
} as const;

// Success messages
export const AUDIENCE_SUCCESS_MESSAGES = {
	AUDIENCE_CREATED: "Audience group created successfully",
	AUDIENCE_DELETED: "Audience group deleted successfully",
	SEARCH_COMPLETED: "Search completed successfully",
} as const;

// LEGACY: Old constants kept for backward compatibility
export const AUDIENCE_CONSTANTS = {
	// Query keys for React Query
	QUERY_KEY_AUDIENCES: "AUDIENCES",
	QUERY_KEY_AUDIENCE_SEARCH: "AUDIENCE_SEARCH",

	// SSE event names
	SSE_AUDIENCE: "audience",

	// API paths
	API_BASE: "/api/audiences",

	// UI constants
	CUSTOM_GREEN: "#52c41a",
	CUSTOM_RED: "#f5222d",

	// Messages
	SUCCESS_CREATE_MSG: "オーディエンスを作成しました",
	SUCCESS_DELETE_MSG: "オーディエンスを削除しました",
	WARN_AUDIENCE_NAME_EXIST_MSG: "同じ名前のオーディエンスが既に存在します", // Original: このオーディエンス名はすでに存在しています
	WARN_AUDIENCE_NOT_MATCH_MSG: "検索条件が一致しません。再度検索してください。", // Original: 検索条件が変更されています。一度検索ボタンクリックしてその後作成してください。
	WARN_AUDIENCE_COUNT_ZERO_MSG: "対象となるメンバーが見つかりません",
	ERROR_SESSION_MSG: "セッションの有効期限が切れました",
	ERROR_SYSTEM_MSG: "システムエラーが発生しました",

	// Response codes
	RESPONSE_SUCCESS: 200,
	RESPONSE_CONFLICT_ERROR: 409,
	RESPONSE_PERMISSION_ERROR: 403,
	RESPONSE_SESSION_ERROR: 401,
	RESPONSE_SYSTEM_ERROR: 500,

	// Filter labels
	FILTER_LABELS: {
		createdAtMin: "友だち追加日から",
		createdAtMax: "友だち追加日まで",
		memberSinceMin: "会員登録日から",
		memberSinceMax: "会員登録日まで",
		lastVisitMin: "最終来店日から",
		lastVisitMax: "最終来店日まで",
		isFriends: {
			label: "ブロック状態",
			0: { label: "ブロックユーザーのみ" },
			1: { label: "ブロックユーザーを含まない" },
		},
		isRegistered: {
			label: "会員登録状態",
			true: { label: "会員登録あり" },
			false: { label: "会員登録なし" },
		},
		messages: {
			label: "LINEチャット",
			unread: { label: "未読" },
			read: { label: "既読" },
		},
	},
};
